import { useTranslation } from 'react-i18next';
import { Settings, DollarSign, AlertCircle, Save } from 'lucide-react';

export const AdminSettingsTab = ({
  priceSettings, setPriceSettings, settingsMessage, savingSettings,
  handleSaveSettings, resetToDefaults
}) => {
  const { t } = useTranslation();

  const exampleDistance = 500;
  const exampleMin = (exampleDistance * Number(priceSettings.min_per_km ?? 0)).toFixed(2);
  const exampleRecommended = (exampleDistance * Number(priceSettings.recommended_per_km ?? 0)).toFixed(2);
  const exampleMax = (exampleDistance * Number(priceSettings.max_per_km ?? 0)).toFixed(2);

  return (
    <div className="settings-content" data-testid="admin-settings-tab">
      <div className="settings-card">
        <div className="settings-header">
          <Settings size={32} />
          <h2>{t('admin.settings.title')}</h2>
          <p>{t('admin.settings.description')}</p>
        </div>
        
        {settingsMessage && (
          <div className={`message-box ${settingsMessage.type}`}>{settingsMessage.text}</div>
        )}
        
        <form onSubmit={handleSaveSettings} className="settings-form">
          <div className="info-box">
            <AlertCircle size={20} />
            <div><strong>{t('admin.settings.important')}:</strong> {t('admin.settings.importantText')}</div>
          </div>
          
          <div className="settings-grid">
            <div className="setting-item">
              <label><DollarSign size={18} /> {t('admin.settings.minPricePerKm')}</label>
              <div className="input-with-unit">
                <input type="number" value={priceSettings.min_per_km} step="0.001" min="0.01" max="0.5"
                  onChange={(e) => setPriceSettings({...priceSettings, min_per_km: parseFloat(e.target.value)})} required />
                <span className="unit">€/km</span>
              </div>
            </div>
            
            <div className="setting-item highlighted">
              <label><DollarSign size={18} /> {t('admin.settings.recommendedPricePerKm')}</label>
              <div className="input-with-unit">
                <input type="number" value={priceSettings.recommended_per_km} step="0.001" min="0.02" max="0.5"
                  onChange={(e) => setPriceSettings({...priceSettings, recommended_per_km: parseFloat(e.target.value)})} required />
                <span className="unit">€/km</span>
              </div>
            </div>
            
            <div className="setting-item">
              <label><DollarSign size={18} /> {t('admin.settings.maxPricePerKm')}</label>
              <div className="input-with-unit">
                <input type="number" value={priceSettings.max_per_km} step="0.001" min="0.03" max="1.0"
                  onChange={(e) => setPriceSettings({...priceSettings, max_per_km: parseFloat(e.target.value)})} required />
                <span className="unit">€/km</span>
              </div>
            </div>
          </div>
          
          <div className="example-preview">
            <h3>{t('admin.settings.exampleTitle', { distance: exampleDistance })}</h3>
            <div className="example-prices">
              <div className="example-price"><span className="label">{t('admin.settings.exampleMinimum')}</span><span className="value">{exampleMin}€</span></div>
              <div className="example-price highlighted"><span className="label">{t('admin.settings.exampleRecommended')}</span><span className="value">{exampleRecommended}€</span></div>
              <div className="example-price"><span className="label">{t('admin.settings.exampleMaximum')}</span><span className="value">{exampleMax}€</span></div>
            </div>
          </div>
          
          <div className="settings-actions">
            <button type="button" onClick={resetToDefaults} className="btn-secondary">{t('admin.settings.resetDefaults')}</button>
            <button type="submit" className="btn-primary" disabled={savingSettings}>
              <Save size={18} /> {savingSettings ? t('admin.settings.saving') : t('admin.settings.save')}
            </button>
          </div>
        </form>
        
        <div className="legal-info">
          <h3>{t('admin.settings.legalInfo')}</h3>
          <ul>
            <li><strong>{t('admin.settings.legalCostSharing')}</strong></li>
            <li><strong>{t('admin.settings.legalCommercial')}</strong></li>
            <li><strong>{t('admin.settings.legalBlablacar')}</strong></li>
          </ul>
        </div>
      </div>
    </div>
  );
};
